import {
  IContentControlStudioViewModel,
  IGetVideoCallbacksContext,
} from 'app/js/src/domain/content-control/studio';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { ContentControlledEditingDirective } from 'app/js/src/domain/content-control/editing-directive';
import {
  MillisecondsToFormattedDuration,
  MillisecondsToFormattedTimecode,
} from 'app/js/src/domain/common/ui-utils';
import { ContentControlCanvasService } from "app/js/src/infrastructure/content-control/content-control-canvas.service";
import { ContentControlStudioBaseViewModel } from "./content-control-studio-base-view-model";
import { StudioAudioManager } from "./helpers/studio-audio-manager";
import { StudioAudioManagerVod } from "./helpers/studio-audio-manager-vod";
import { VideoClip } from './content-control-studio-actions/content-control-studio-actions.component';

/**
 * VOD (Video-On-Demand) studio view model.
 *
 * Extends the base view model with VOD-specific video setup, audio/subtitle
 * track management, and time-format logic. Unlike the live variant, VOD
 * operates on video-relative times (not UTC timestamps) and supports
 * playback speed changes.
 */
export class ContentControlStudioVODViewModel
  extends ContentControlStudioBaseViewModel
{
  /**
   * @param canvasService - Service for canvas-based graphics rendering.
   * @param _videoSource - The VOD video source element wrapping the HTML video.
   * @param componentContext - Callbacks and references from the host component.
   */
  constructor(
    canvasService: ContentControlCanvasService,
    protected _videoSource: IContentControlVideoSource,
    componentContext: IGetVideoCallbacksContext,
  ) {
    super(canvasService, componentContext);
  }

  /** Returns the single VOD video source, ignoring any active events filter. */
  protected _getVideoSource(_activeEvents?: ContentControlledEditingDirective[]): IContentControlVideoSource {
    return this._mainVideoElement;
  }

  /** Creates a VOD-specific audio manager that uses the main video element as its source. */
  protected createAudioManager(): StudioAudioManager {
    return new StudioAudioManagerVod(
      () => this._getVideoSource(),
      this._videoContainerElement,
      this._manager,
      this._timelineRef,
      this._playback,
    );
  }

  /**
   * Configures the VOD video element with its source URL and event listeners.
   *
   * Registers handlers for:
   * - `loadeddata` — triggers the initial video-loaded flow.
   * - `play` — synchronises external audio tracks with the video.
   * - `pause` — pauses external audio and snaps the video to the nearest frame.
   * - `timeupdate` — keeps external audio in sync during playback (>0.1 s drift).
   * - `seeking` — syncs external audio and records the last seeked position.
   */
  setupVideo(): void {
    this._videoSource.addSource(this._manager.videoUrl);
    this._videoSource.load();

    this._videoSource.addEventListener('loadeddata', () => this.onVideoLoadedEventHandler());

    this._videoSource.addEventListener('play', () => this.syncAudio());

    this._videoSource.addEventListener('pause', () => {
      const isVideoAudioTrack = IContentControlStudioViewModel.VK === this.selectedAudioOption;
      if (isVideoAudioTrack === false) {
        this.availableAudioTracks[this.selectedAudioOption].mediaElement.pause();
      }
      const timeline = this.timelineRef;
      const videoSource = this.getVideoSource();
      timeline.currentVideoPlayTimeSecs() < timeline.currentVideoTimeSecs(timeline.isFitSegment, timeline.isEditedTimeline) &&
        videoSource?.seekToVideoPlayerRelativeTimeInSecs(videoSource?.currentPlayTimeAsClosestFrameSecs());
    });

    this._videoSource.addEventListener('timeupdate', () => {
      const isVideoAudioTrack = IContentControlStudioViewModel.VK === this.selectedAudioOption;
      if (isVideoAudioTrack === false) {
        const cat = this.availableAudioTracks[this.selectedAudioOption];
        if (
          Math.abs(
            cat.mediaElement.currentTime - this.timelineRef.currentVideoPlayTimeSecs(),
          ) > 0.1
        ) {
          cat.mediaElement.currentTime = this.timelineRef.currentVideoPlayTimeSecs();
        }
      }
    });

    this._videoSource.addEventListener('seeking', () => {
      const isVideoAudioTrack = IContentControlStudioViewModel.VK === this.selectedAudioOption;
      for (const audioElement of Object.values(this.availableAudioTracks)) {
        if (isVideoAudioTrack === false) {
          (audioElement as { mediaElement: HTMLMediaElement }).mediaElement.currentTime = this.timelineRef.currentVideoPlayTimeSecs();
        }
      }
      this._componentContext.lastSeekedPosition = this.timelineRef.currentVideoPlayTimeSecs();
    });
  }
  /**
   * Loads all subtitle files from the manager and builds the subtitle options list.
   * Prepends the default "no subtitles" key as the first option.
   * @param subtitleOptions - Mutable array to populate (cleared and rebuilt).
   * @returns The populated subtitle options array with language names.
   */
  setupSubtitles(subtitleOptions: string[]): string[] {
    subtitleOptions = this._manager.subtitleFiles.map((x) => {
      this._manager.loadSubtitle(x.language, x.url, x.fps);
      return x.language;
    });
    subtitleOptions.unshift(IContentControlStudioViewModel.SK);
    return subtitleOptions;
  }
  /**
   * Loads all audio files from the manager and builds the audio options list.
   * Prepends the "video audio" key as the first option, then adds each
   * external audio track to the video source.
   * @param audioOptions - Mutable array to populate.
   * @returns The populated audio options array with language names.
   */
  setupAudio(audioOptions: string[]): string[] {
    audioOptions.unshift(IContentControlStudioViewModel.VK);
    for (const audio of this._manager.audioFiles) {
      audioOptions.push(audio.language);
      this._videoSource.addAudioTrack(audio.url, audio.language);
    }
    return audioOptions;
  }

  /**
   * Returns the end time reference for a mark-in operation.
   * For VOD, simply converts the current video time from milliseconds to seconds.
   * @param currentVideoTimeInMs - Current video playback time in milliseconds.
   */
  getEndTimeForMarkIn(currentVideoTimeInMs: number): number {
    return currentVideoTimeInMs / 1000;
  }

  /**
   * Converts a player-relative time (seconds) to a timestamp in milliseconds.
   * For VOD, this is a simple seconds-to-milliseconds multiplication (no UTC offset).
   * @param playerTimeSecs - Playback time in seconds.
   */
  convertPlayerTimeToTimestampMs(playerTimeSecs: number): number {
    return playerTimeSecs * 1000;
  }

  /** VOD mark-in uses video-relative time, not UTC timestamps. Always returns `false`. */
  shouldUseTimestampForMarkIn(): boolean {
    return false;
  }

  /**
   * Returns an empty array — VOD does not need to prune directives that have
   * fallen outside the playback window (unlike live, where the HLS sliding
   * window can invalidate old directives).
   * @param directives - The current editing directives (unused).
   * @param selectedSegmentId - The currently selected segment ID (unused).
   */
  cleanupOldDirectives(directives: ContentControlledEditingDirective[], selectedSegmentId?: number): ContentControlledEditingDirective[] {
    return [];
  }

  /**
   * Formats a time value for UI display.
   *
   * When the manager is configured for frame-based display and `justMinutesAndSeconds`
   * is not set, returns a SMPTE-style timecode (HH:MM:SS:FF). Otherwise falls back
   * to a duration format (HH:MM:SS.mmm or MM:SS).
   * @param milliseconds - The time value to format.
   * @param justMinutesAndSeconds - If `true`, forces MM:SS format regardless of settings.
   */
  timeFormatFunction(milliseconds: number, justMinutesAndSeconds?: boolean): string {
    const manager = this._manager;
    if (!manager) {
      return MillisecondsToFormattedDuration(milliseconds, justMinutesAndSeconds);
    }
    const videoFps = manager.videoFps;
    const displayInFrames = manager.displayContentControlStudioTimeInFrames;
    if (displayInFrames && !justMinutesAndSeconds && videoFps != null && videoFps > 0) {
      return MillisecondsToFormattedTimecode(milliseconds, videoFps);
    }
    return MillisecondsToFormattedDuration(milliseconds, justMinutesAndSeconds);
  }

  /** VOD supports variable playback speed (0.25x–2x). Always returns `true`. */
  supportsPlaybackSpeed(): boolean {
    return true;
  }

  /**
   * Called once the video metadata has loaded. Seeks to the beginning of the video.
   * @param video - The loaded video source element.
   */
  onVideoLoaded(video: IContentControlVideoSource): void {
    video.seekToVideoPlayerRelativeTimeInSecs(0);
  }

  /** Returns `0` — VOD does not track UTC timestamps for the current playback position. */
  getCurrentTimeForUIAsTimestampMs(): number {
    return 0;
  }

  /** No-op — VOD has no live edge to seek to. */
  moveToLiveEdge(): void {
    // No-op for VOD
  }

  /** No-op — VOD does not support adding video clips at runtime. */
  public onVideoClipAdd(_clip: VideoClip): void {
    // No-op — VOD does not support video clips
  }
}
